import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiLogOut, FiSave, FiBriefcase } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Internship';
  experience: string;
  salaryRange: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
  skills: string[];
  benefits: string[];
  postedDate: string;
  status: 'active' | 'closed';
}

const CareersDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    type: 'Full-time' as 'Full-time' | 'Part-time' | 'Contract' | 'Internship',
    experience: '',
    salaryRange: '',
    description: '',
    responsibilities: '',
    requirements: '',
    skills: '',
    benefits: '',
    status: 'active' as 'active' | 'closed'
  });

  useEffect(() => {
    // Check authentication
    const authToken = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!authToken || userType !== 'hr') {
      navigate('/login');
      return;
    }

    // Load jobs from localStorage
    const savedJobs = localStorage.getItem('jobPosts');
    if (savedJobs) {
      setJobs(JSON.parse(savedJobs));
    } else {
      // Demo jobs
      const demoJobs: Job[] = [
        {
          id: '1',
          title: 'Senior Digital Marketing Manager',
          department: 'Marketing',
          location: 'Mumbai, India',
          type: 'Full-time',
          experience: '5-7 years',
          salaryRange: '₹12-18 LPA',
          description: 'We are looking for an experienced Digital Marketing Manager to lead our marketing team and drive online growth strategies.',
          responsibilities: [
            'Develop and execute comprehensive digital marketing strategies',
            'Manage SEO, SEM, and social media campaigns',
            'Lead a team of marketing professionals',
            'Analyze campaign performance and optimize ROI'
          ],
          requirements: [
            'Bachelor\'s degree in Marketing or related field',
            'Minimum 5 years of digital marketing experience',
            'Proven track record of successful campaigns',
            'Strong analytical and leadership skills'
          ],
          skills: ['SEO', 'Google Ads', 'Social Media Marketing', 'Analytics', 'Leadership'],
          benefits: [
            'Competitive salary package',
            'Health insurance',
            'Flexible work hours',
            'Professional development opportunities'
          ],
          postedDate: '2025-01-15',
          status: 'active'
        },
        {
          id: '2',
          title: 'Frontend Developer',
          department: 'IT',
          location: 'Remote',
          type: 'Full-time',
          experience: '2-4 years',
          salaryRange: '₹8-12 LPA',
          description: 'Join our development team to build cutting-edge web applications using modern frontend technologies.',
          responsibilities: [
            'Develop responsive web applications using React',
            'Collaborate with designers and backend developers',
            'Write clean, maintainable code',
            'Participate in code reviews'
          ],
          requirements: [
            'Bachelor\'s degree in Computer Science or equivalent',
            '2+ years of React experience',
            'Strong knowledge of HTML, CSS, JavaScript',
            'Experience with TypeScript and modern build tools'
          ],
          skills: ['React', 'TypeScript', 'Tailwind CSS', 'Git', 'RESTful APIs'],
          benefits: [
            'Remote work flexibility',
            'Learning and development budget',
            'Latest tech equipment',
            'Team outings and events'
          ],
          postedDate: '2025-01-20',
          status: 'active'
        }
      ];
      setJobs(demoJobs);
      localStorage.setItem('jobPosts', JSON.stringify(demoJobs));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userType');
    navigate('/login');
  };

  const handleSaveJobs = (updatedJobs: Job[]) => {
    setJobs(updatedJobs);
    localStorage.setItem('jobPosts', JSON.stringify(updatedJobs));
  };

  const handleCreateNew = () => {
    setEditingJob(null);
    setFormData({
      title: '',
      department: '',
      location: '',
      type: 'Full-time',
      experience: '',
      salaryRange: '',
      description: '',
      responsibilities: '',
      requirements: '',
      skills: '',
      benefits: '',
      status: 'active'
    });
    setShowEditor(true);
    setPreviewMode(false);
  };

  const handleEdit = (job: Job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      department: job.department,
      location: job.location,
      type: job.type,
      experience: job.experience,
      salaryRange: job.salaryRange,
      description: job.description,
      responsibilities: job.responsibilities.join('\n'),
      requirements: job.requirements.join('\n'),
      skills: job.skills.join(', '),
      benefits: job.benefits.join('\n'),
      status: job.status
    });
    setShowEditor(true);
    setPreviewMode(false);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      const updatedJobs = jobs.filter(job => job.id !== id);
      handleSaveJobs(updatedJobs);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newJob: Job = {
      id: editingJob?.id || Date.now().toString(),
      title: formData.title,
      department: formData.department,
      location: formData.location,
      type: formData.type,
      experience: formData.experience,
      salaryRange: formData.salaryRange,
      description: formData.description,
      responsibilities: formData.responsibilities.split('\n').filter(item => item.trim()),
      requirements: formData.requirements.split('\n').filter(item => item.trim()),
      skills: formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill),
      benefits: formData.benefits.split('\n').filter(item => item.trim()),
      postedDate: editingJob?.postedDate || new Date().toISOString().split('T')[0],
      status: formData.status
    };

    let updatedJobs;
    if (editingJob) {
      updatedJobs = jobs.map(job => job.id === editingJob.id ? newJob : job);
    } else {
      updatedJobs = [...jobs, newJob];
    }

    handleSaveJobs(updatedJobs);
    setShowEditor(false);
  };

  const getCurrentJobForPreview = (): Job => {
    return {
      id: editingJob?.id || 'preview',
      title: formData.title || 'Job Title',
      department: formData.department || 'Department',
      location: formData.location || 'Location',
      type: formData.type,
      experience: formData.experience || 'Experience',
      salaryRange: formData.salaryRange || 'Salary Range',
      description: formData.description || 'Job description will appear here...',
      responsibilities: formData.responsibilities.split('\n').filter(item => item.trim()),
      requirements: formData.requirements.split('\n').filter(item => item.trim()),
      skills: formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill),
      benefits: formData.benefits.split('\n').filter(item => item.trim()),
      postedDate: new Date().toISOString().split('T')[0],
      status: formData.status
    };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Careers Dashboard</h1>
            <p className="text-sm text-gray-600">Manage job postings</p>
          </div>
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCreateNew}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg transition-all duration-300"
            >
              <FiPlus className="w-5 h-5" />
              New Job
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLogout}
              className="bg-red-500 text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg transition-all duration-300"
            >
              <FiLogOut className="w-5 h-5" />
              Logout
            </motion.button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {!showEditor ? (
          /* Jobs List */
          <div className="space-y-4">
            {jobs.map((job) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-gray-800">{job.title}</h3>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        job.status === 'active' 
                          ? 'bg-green-50 text-green-600' 
                          : 'bg-red-50 text-red-600'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1">
                        <FiBriefcase className="w-4 h-4" />
                        {job.department}
                      </span>
                      <span>📍 {job.location}</span>
                      <span>💼 {job.type}</span>
                      <span>⏱️ {job.experience}</span>
                      <span>💰 {job.salaryRange}</span>
                    </div>
                    <p className="text-gray-600 mb-3 line-clamp-2">{job.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(job)}
                      className="bg-blue-50 text-blue-600 p-3 rounded-lg hover:bg-blue-100 transition-colors duration-300"
                      title="Edit"
                    >
                      <FiEdit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(job.id)}
                      className="bg-red-50 text-red-600 p-3 rounded-lg hover:bg-red-100 transition-colors duration-300"
                      title="Delete"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  Posted: {new Date(job.postedDate).toLocaleDateString()}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          /* Editor Form */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingJob ? 'Edit Job' : 'Create New Job'}
                </h2>
                <button
                  onClick={() => setShowEditor(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Job Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Department & Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Department *
                    </label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select</option>
                      <option value="Marketing">Marketing</option>
                      <option value="IT">IT</option>
                      <option value="Sales">Sales</option>
                      <option value="HR">HR</option>
                      <option value="Operations">Operations</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Mumbai, Remote"
                      required
                    />
                  </div>
                </div>

                {/* Type & Experience */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Job Type *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Experience *
                    </label>
                    <input
                      type="text"
                      value={formData.experience}
                      onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 2-4 years"
                      required
                    />
                  </div>
                </div>

                {/* Salary Range */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Salary Range *
                  </label>
                  <input
                    type="text"
                    value={formData.salaryRange}
                    onChange={(e) => setFormData({ ...formData, salaryRange: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., ₹8-12 LPA"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Job Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    required
                  />
                </div>

                {/* Responsibilities */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Responsibilities (one per line) *
                  </label>
                  <textarea
                    value={formData.responsibilities}
                    onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    required
                  />
                </div>

                {/* Requirements */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Requirements (one per line) *
                  </label>
                  <textarea
                    value={formData.requirements}
                    onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    required
                  />
                </div>

                {/* Skills */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Required Skills (comma-separated) *
                  </label>
                  <input
                    type="text"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., React, Node.js, MongoDB"
                    required
                  />
                </div>

                {/* Benefits */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Benefits (one per line)
                  </label>
                  <textarea
                    value={formData.benefits}
                    onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="active"
                        checked={formData.status === 'active'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' })}
                        className="mr-2"
                      />
                      Active
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="closed"
                        checked={formData.status === 'closed'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as 'closed' })}
                        className="mr-2"
                      />
                      Closed
                    </label>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-4 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
                  >
                    <FiSave className="w-5 h-5 inline mr-2" />
                    {editingJob ? 'Update' : 'Create'}
                  </motion.button>
                  <button
                    type="button"
                    onClick={() => setShowEditor(false)}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>

            {/* Preview Section */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg p-8 sticky top-4"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Live Preview</h3>
                <FiEye className="w-5 h-5 text-gray-600" />
              </div>
              
              {(() => {
                const previewJob = getCurrentJobForPreview();
                return (
                  <div className="space-y-4">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-800 mb-2">{previewJob.title}</h1>
                      <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                        <span className="bg-blue-50 px-3 py-1 rounded-full">{previewJob.department}</span>
                        <span className="bg-green-50 px-3 py-1 rounded-full">{previewJob.location}</span>
                        <span className="bg-purple-50 px-3 py-1 rounded-full">{previewJob.type}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="font-semibold text-gray-700">Experience:</span>
                        <p className="text-gray-600">{previewJob.experience}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Salary:</span>
                        <p className="text-gray-600">{previewJob.salaryRange}</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                      <p className="text-gray-600 text-sm">{previewJob.description}</p>
                    </div>

                    {previewJob.responsibilities.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Responsibilities</h4>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
                          {previewJob.responsibilities.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {previewJob.requirements.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Requirements</h4>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
                          {previewJob.requirements.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {previewJob.skills.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Skills</h4>
                        <div className="flex flex-wrap gap-2">
                          {previewJob.skills.map((skill, index) => (
                            <span key={index} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {previewJob.benefits.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
                          {previewJob.benefits.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareersDashboard;
